import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

const MAX_MENU_HEIGHT = 384; // matches Tailwind max-h-96
const MENU_GAP = 4;
const MIN_SPACE_BELOW = 160; // flip upward if there is less room than this below

interface AnchoredMenu<T extends HTMLElement> {
  menuRef: React.RefObject<T>;
  menuStyle: React.CSSProperties;
}

/**
 * Positions a dropdown menu that is rendered in a portal (on document.body) so
 * it is not clipped by an ancestor's overflow — e.g. a dialog. The menu is
 * fixed-positioned against the anchor element and flips upward when there is
 * little room below.
 *
 * While open it tracks the anchor on every animation frame, so the menu follows
 * the trigger through scroll, resize AND layout reflows (e.g. a sibling panel
 * appearing and shifting the trigger). State only updates when the geometry
 * actually changes, so there are no per-frame re-renders.
 *
 * @param anchorRef ref to the trigger element the menu should align with.
 * @param isOpen whether the menu is currently shown.
 * @param maxMenuHeight cap for the menu height in px (defaults to max-h-96).
 * @returns a `menuRef` to attach to the portalled menu and the computed `menuStyle`.
 */
export const useAnchoredMenu = <T extends HTMLElement>(
  anchorRef: React.RefObject<HTMLElement>,
  isOpen: boolean,
  maxMenuHeight: number = MAX_MENU_HEIGHT,
): AnchoredMenu<T> => {
  const menuRef = useRef<T>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  // signature of the last applied geometry, to skip redundant state updates
  const lastGeometryRef = useRef<string>("");

  const updateMenuPosition = useCallback((): void => {
    const anchor = anchorRef.current;

    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const { innerHeight, innerWidth } = window;
    const geometry = `${rect.top}|${rect.left}|${rect.width}|${rect.bottom}|${innerWidth}|${innerHeight}`;

    if (geometry === lastGeometryRef.current) return;

    lastGeometryRef.current = geometry;

    const spaceBelow = innerHeight - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;
    const openUp = spaceBelow < MIN_SPACE_BELOW && spaceAbove > spaceBelow;

    const maxHeight = Math.max(
      0,
      Math.min(maxMenuHeight, openUp ? spaceAbove : spaceBelow),
    );

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      maxHeight,
      ...(openUp
        ? { bottom: innerHeight - rect.top + MENU_GAP }
        : { top: rect.bottom + MENU_GAP }),
    });
  }, [anchorRef, maxMenuHeight]);

  // useLayoutEffect so the first position is applied before the browser paints —
  // otherwise the first open flashes at a default (unstyled) position. While
  // open, an rAF loop keeps the menu glued to the trigger through any movement.
  useLayoutEffect(() => {
    if (!isOpen) {
      lastGeometryRef.current = "";

      return;
    }

    let frame = 0;

    const tick = (): void => {
      updateMenuPosition();
      frame = requestAnimationFrame(tick);
    };

    updateMenuPosition();
    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [isOpen, updateMenuPosition]);

  return { menuRef, menuStyle };
};
